import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GlassCard } from "@/components/ui/glass-card";
import { Shield, AlertTriangle, Package, Monitor } from "lucide-react";
import { CVSSDistributionCard } from "@/components/vulnerabilities/shared/CVSSDistributionCard";
import { AppVulnerabilitySummary, AppVulnerability } from "@/types/appVulnerability";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from "recharts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface AppVulnerabilitySummaryCardsProps {
  summary: AppVulnerabilitySummary;
  vulnerabilities: AppVulnerability[];
}

export function AppVulnerabilitySummaryCards({ summary, vulnerabilities }: AppVulnerabilitySummaryCardsProps) {
  const severityData = [
    { name: 'Critical', value: summary.critical, color: 'hsl(0 84.2% 60.2%)' },
    { name: 'High', value: summary.high, color: 'hsl(30 80% 55%)' },
    { name: 'Medium', value: summary.medium, color: 'hsl(45 90% 55%)' },
    { name: 'Low', value: summary.low, color: 'hsl(220 70% 50%)' },
  ].filter(item => item.value > 0);

  return (
    <div className="space-y-4 mb-6">
      {/* CVSS Distribution - Full Width */}
      <CVSSDistributionCard vulnerabilities={vulnerabilities} />
      
      {/* Other Cards - 4 Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Vulnerabilities by Severity */}
      <GlassCard className="group">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">By Severity</CardTitle>
          <div className="p-2 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 backdrop-blur-sm border border-primary/20 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300">
            <Shield className="h-4 w-4 text-primary" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[140px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={severityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={50}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {severityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    padding: '8px 12px',
                  }}
                  labelStyle={{ 
                    color: 'hsl(var(--popover-foreground))',
                    fontWeight: 600,
                  }}
                  formatter={(value: number) => [value, 'Count']}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={20}
                  iconSize={8}
                  formatter={(value) => value}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="text-center mt-2">
            <p className="text-2xl font-bold">{summary.total}</p>
            <p className="text-xs text-muted-foreground">Total Vulnerabilities</p>
          </div>
        </CardContent>
      </GlassCard>

      {/* Actively Exploited */}
      <GlassCard className="group">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Actively Exploited</CardTitle>
          <div className="p-2 rounded-lg bg-gradient-to-br from-destructive/10 to-destructive/5 backdrop-blur-sm border border-destructive/20 group-hover:from-destructive/20 group-hover:to-destructive/10 transition-all duration-300">
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[140px]">
            <div className="relative">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="hsl(var(--muted))"
                  strokeWidth="10"
                  fill="none"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="hsl(30 80% 55%)"
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray={`${(summary.actively_exploited_percentage || 0) * 2.51} 251.33`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold">
                  {(summary.actively_exploited_percentage || 0).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
          <div className="text-center mt-2">
            <p className="text-xl font-semibold">{summary.actively_exploited_count || 0}</p>
            <p className="text-xs text-muted-foreground">Known Exploits</p>
          </div>
        </CardContent>
      </GlassCard>

      {/* Affected Applications */}
      <GlassCard className="group">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Affected Apps</CardTitle>
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-500/5 backdrop-blur-sm border border-blue-500/20 group-hover:from-blue-500/20 group-hover:to-blue-500/10 transition-all duration-300">
            <Package className="h-4 w-4 text-blue-500" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[140px]">
            <p className="text-4xl font-bold">{summary.affected_apps_count || 0}</p>
            <p className="text-sm text-muted-foreground mt-2">Applications with vulnerabilities</p>
          </div>
          <div className="text-center mt-2 pt-2 border-t">
            <p className="text-xs text-muted-foreground">Requires security updates</p>
          </div>
        </CardContent>
      </GlassCard>

      {/* Affected Devices */}
      <GlassCard className="group">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Devices at Risk</CardTitle>
          <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-500/5 backdrop-blur-sm border border-purple-500/20 group-hover:from-purple-500/20 group-hover:to-purple-500/10 transition-all duration-300">
            <Monitor className="h-4 w-4 text-purple-500" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[140px]">
            <p className="text-4xl font-bold text-[hsl(30_80%_55%)]">{summary.affected_devices_count || 0}</p>
            <p className="text-sm text-muted-foreground mt-2">Devices need attention</p>
          </div>
          <div className="text-center mt-2 pt-2 border-t">
            <p className="text-xs text-muted-foreground">Running vulnerable apps</p>
          </div>
        </CardContent>
      </GlassCard>
      </div>
    </div>
  );
}
